import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { finalize } from 'rxjs/operators';
import { defer, forkJoin } from 'rxjs';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { EmployeeGrievancesService } from './services/employee-grievances.service';
import { EmployeeGrievance, EmployeeGrievanceVerbose } from './models/employee-grievance.model';
import { api_routes, db_tables } from '@app/consts';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TablePermissionsService } from '@app/core/services/table-permissions/table-permissions.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-employee-grievances',
  templateUrl: './employee-grievances.component.html',
  styleUrls: ['./employee-grievances.component.scss']
})
export class EmployeeGrievancesComponent implements OnInit {
  @Input() formIds: string[];
  employeeID: string;

  public columns: any[] = [
    { field: "type", subField: "text", title: "Type", type: "navigation", tableId: "tfi_GrievanceType" },
    { field: "shortDescription", title: "Short Description", tableId: "tfi_GrievanceShortDescription" },
    { field: "date", title: "Date", type: "date", tableId: "tfi_GrievanceDate" },
    { field: "filedBy", subField: "text", title: "filed By", tableId: "tfi_GrievanceFiledBy" },
    { field: "status", subField: "text", title: "Status", tableId: "tfi_GrievanceStatus" },
    { field: "finalResolution", subField: "text", title: "Final Resolution", tableId: "tfi_GrievanceFinalResolution" },
  ];
  public bindingType: String = "array";
  public view: any;
  public gridData: any;
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 5;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: MatDialogRef<FormGeneratorDialogComponent>;
  formId: string = 'frm_vmmI4Zj0zefPLq'
  changeReasonFormId: string = 'frm_tXry4lyrw3xK9e';
  formValid: boolean;
  private sortString: string = 'date-desc';
  isLoadingPermissions: boolean = true;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  accessAndHistoryURL: string = `${api_routes.TALENT}/[employeeId]/${api_routes.GRIEVANCES}/[entityId]`;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private employeeGrievanceService: EmployeeGrievancesService,
    private snackbarService: SnackbarService,
    private tablePermissionsService: TablePermissionsService,
    private route: ActivatedRoute,
    private overlayService: OverlayService
  ) {
    this.route.paramMap.subscribe(
      params => this.employeeID = params.get("id")
    )
  }

  ngOnInit(): void {
    this.view = this.getGrievances();
    this.getTargetEmployeeTablePermissions();
  }

  getTargetEmployeeTablePermissions() {
    this.isLoadingPermissions = true;

    let currentFetchedData = this.tablePermissionsService.getFetchedTargetEmployeeTablePermissions(this.employeeID);

    // if the current table permissions that are saved are for this employee use those, else fetch the new permissions.
    if(currentFetchedData !== null){
      this.targetEmployeeTablePermissions = currentFetchedData;
      this.isLoadingPermissions = false;
    }
    else {
      this.tablePermissionsService.getTargetEmployeeTablePermissions(this.employeeID)
      .pipe(
        finalize(() => {
            this.isLoadingPermissions = false;
        })
      )
      .subscribe(
        res => {
          this.targetEmployeeTablePermissions = res;
        }
      );
    }
  }

  getGrievances() {
    this.isLoading = true;

    this.employeeGrievanceService.getGrievances(this.employeeID, this.pageSize, this.skip, this.sortString)
      .pipe(
        finalize( () => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
          this.gridData = res.data;
        }
      );
  }

  getGrievance(grievance?: EmployeeGrievance) {
    if(grievance === undefined){
      this.openFormDialog();
    }
    else {
      this.isLoading = true;

      this.employeeGrievanceService.getGrievance(this.employeeID, grievance.id)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(
        res => {
          this.openFormDialog(res);
        }
      );
    }
  }

  openFormDialog(grievance?: EmployeeGrievanceVerbose) {
    let formData = {
      id: grievance ? grievance.id : null,
      asOf: moment().format(),
      type: grievance && grievance.type ? grievance.type.id : null,
      date: grievance ? grievance.date : null,
      filedBy: grievance && grievance.filedBy ? grievance.filedBy.id : null,
      shortDescription: grievance ? grievance.shortDescription : null,
      description: grievance ? grievance.description : null,
      status: grievance && grievance.status ? grievance.status.id : null,
      finalResolution: grievance && grievance.finalResolution ? grievance.finalResolution.id : null,
      changeReason: '',
      changeReasonComments: ''
    };

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('Grievance')}`,
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any){
    this.overlayService.show();

    if(!formData.id){
      this.employeeGrievanceService.createGrievance(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getGrievances();
        }
      );
    }
    else {
      this.employeeGrievanceService.updateGrievance(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getGrievances();
        }
      );
    }
  }

  deleteGrievances(GrievanceIds: string[]) {
    const observables = GrievanceIds.map(selectedItem => defer(() => this.employeeGrievanceService.deleteGrievance(this.employeeID, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.view = this.getGrievances();
        this.clearSelectedItems = !this.clearSelectedItems;
          this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.view = this.getGrievances();
  }

}




